import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import {
  buscarEmpresasConciliaveis,
  buscarFilaConciliacao,
  buscarMovimentos,
  confirmarConciliacao,
  desfazerConciliacao,
} from "./api";
import type {
  EmpresaConciliavel,
  FilaStatus,
  ItemFilaConciliacao,
  MovimentoConciliacao,
  MovimentoConciliado,
} from "./tipos";

/*
 * Hooks de server-state da conciliacao (FE-3 CP-A — ADR 0026 §Estado / TanStack
 * Query). Toda leitura/escrita de servidor passa pelo Query: cache, dedup e
 * revalidacao com a config conservadora do `criarQueryClient` (staleTime 30s,
 * retry 1 em query; `mutations:{retry:0}`).
 *
 * Convencoes de queryKey: prefixo `"conciliacao"` + recurso + parametros que
 * afetam o resultado (empresaId, status). Chaves estaveis garantem cache hit e
 * refetch correto ao trocar empresa/status.
 *
 * `enabled` (gate de disparo): impede o fetch enquanto a empresa essencial nao
 * existe (nao selecionada). Sem `enabled`, a query da fila dispararia com `null`.
 */

/** Empresas conciliaveis pelo escopo do usuario. */
export function useEmpresasConciliaveis(): UseQueryResult<EmpresaConciliavel[]> {
  return useQuery({
    queryKey: ["conciliacao", "empresas"],
    queryFn: buscarEmpresasConciliaveis,
  });
}

/**
 * Fila de conciliacao de uma empresa por status. So dispara quando ha empresa
 * selecionada (`empresaId !== null`) — antes disso a query fica ociosa
 * (`enabled: false`), sem fetch nem erro. A queryKey inclui empresaId+status:
 * trocar qualquer um e uma query distinta no cache.
 */
export function useFilaConciliacao(
  empresaId: string | null,
  status: FilaStatus,
): UseQueryResult<ItemFilaConciliacao[]> {
  return useQuery({
    queryKey: ["conciliacao", "fila", empresaId, status],
    // `empresaId as string` e seguro: `enabled` garante que a queryFn so roda
    // com empresa nao-nula. O TanStack so invoca a queryFn quando `enabled` e true.
    queryFn: () => buscarFilaConciliacao(empresaId as string, status),
    enabled: empresaId !== null,
  });
}

/** Argumento da mutacao de confirmacao: o item da fila + o candidato escolhido. */
interface ConfirmarArgs {
  filaId: string;
  candidatoId: string;
}

/**
 * Confirma a conciliacao manual de um item da fila (1a mutacao do frontend).
 *
 * `empresaId` e fixado no hook (a tela ja sabe a empresa em foco); a mutacao
 * recebe `{filaId, candidatoId}` no `mutate`. NO SUCESSO, invalida TODA a fila
 * daquela empresa (`["conciliacao","fila",empresaId]` — prefixo, abrange os dois
 * status): o item confirmado some de `aberta` e aparece em `resolvida` no
 * proximo fetch, sem o painel precisar saber qual status revalidar.
 *
 * NAO seta `retry` aqui: `criarQueryClient` ja aplica `mutations:{retry:0}` —
 * mover dinheiro nao retenta as cegas (evita confirmacao duplicada por retry).
 */
export function useConfirmarConciliacao(
  empresaId: string,
): UseMutationResult<MovimentoConciliacao, unknown, ConfirmarArgs> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ filaId, candidatoId }: ConfirmarArgs) =>
      confirmarConciliacao(empresaId, filaId, { candidato_id: candidatoId }),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ["conciliacao", "fila", empresaId],
      }),
  });
}

/*
 * --- FE-4: Desfazer conciliacao (Tela 4 §10.5) ---------------------------------
 */

/** Filtros de paginacao da lista de movimentos conciliados. */
interface FiltrosMovimentos {
  status: string;
  limit: number;
  offset: number;
}

/**
 * Movimentos JA conciliados de uma empresa (`GET .../conciliacao/movimentos`). So
 * dispara com empresa selecionada (`empresaId !== null` → `enabled`). A queryKey
 * inclui o OBJETO `filtros` (status/limit/offset): trocar paginacao e uma query
 * distinta no cache.
 */
export function useMovimentos(
  empresaId: string | null,
  filtros: FiltrosMovimentos,
): UseQueryResult<MovimentoConciliado[]> {
  return useQuery({
    queryKey: ["conciliacao", "movimentos", empresaId, filtros],
    queryFn: () => buscarMovimentos(empresaId as string, filtros),
    enabled: empresaId !== null,
  });
}

/** Argumento da mutacao de desfazer: o movimento + a justificativa. */
interface DesfazerArgs {
  movimentoId: string;
  justificativa: string;
}

/**
 * Desfaz a conciliacao de um movimento (escrita que REVERTE dinheiro). `empresaId`
 * e fixado no hook; a mutacao recebe `{movimentoId, justificativa}`. NO SUCESSO,
 * invalida TODA a lista de movimentos da empresa
 * (`["conciliacao","movimentos",empresaId]` — prefixo, abrange paginas/status): o
 * movimento desfeito sai da lista "ativa" no proximo fetch.
 *
 * NAO seta `retry`: `criarQueryClient` ja aplica `mutations:{retry:0}` — reverter
 * dinheiro nao retenta as cegas.
 */
export function useDesfazer(
  empresaId: string,
): UseMutationResult<MovimentoConciliacao, unknown, DesfazerArgs> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ movimentoId, justificativa }: DesfazerArgs) =>
      desfazerConciliacao(empresaId, movimentoId, { justificativa }),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ["conciliacao", "movimentos", empresaId],
      }),
  });
}
